import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable } from 'rxjs';
import { IdentificationProvider } from '@shared/enums/identification-provider.enum';
import { Gender } from '@shared/enums/gender.enum';
import { DialogService } from '../shared/services/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { JwtHelperService } from 'projects/dashboard/src/app/modules/auth/services/jwt-helper.service';
import { AbstractControl } from '@angular/forms';
import { environment } from 'projects/forms/src/environments/environment';

export class User {
  dateOfBirth: Date;
  firstName: string;
  lastName: string;
  mobilePhone: string;
  email: string;
  gender?: Gender;
  token: string;
  identityProvider: IdentificationProvider;
  formsAdmin: boolean;

  public clientId: string = environment.keycloak.clientId;

  constructor(jwtToken: string) {
    const jwtPayload = JwtHelperService.parseJwt(jwtToken);


    if(jwtPayload.birthdate) {
      const [day, month, year] = jwtPayload.birthdate?.split('.')
        ?.map((v: string) => parseInt(v));
        this.dateOfBirth = new Date(Date.UTC(year, month - 1, day));
    }
    this.firstName = jwtPayload.given_name;
    this.lastName = jwtPayload.family_name;
    this.mobilePhone = jwtPayload.phone_number;
    this.email = jwtPayload.email;
    this.gender = this.setGender(jwtPayload.gender);
    this.token = jwtToken;
    this.identityProvider = jwtPayload.identity_provider;
    this.formsAdmin = jwtPayload['resource_access']?.[this.clientId]?.roles?.includes('forms-admin') || false;
  }

  setGender(gender: string) {  
    if(['M', 'male'].includes(gender)) {
      return Gender.MALE;
    } else if (['F', 'female'].includes(gender)) {
      return Gender.FEMALE;
    } else {
      return undefined;
    }
  }

  get displayName(): string {
    return `${this.firstName} ${this.lastName}`;
  }
}

@Injectable()
export class IdentificationProviderService {
  private readonly user: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  private readonly isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  constructor(
    protected readonly oauthService: OAuthService,
    protected readonly dialogService: DialogService,
    protected readonly translateService: TranslateService
  ) {
    this.oauthService.events
      .subscribe(() => {
        this.isAuthenticated.next(this.oauthService.hasValidAccessToken());
      });
  }

  public async login(identificationProvider: IdentificationProvider, redirectUri?: string) {
    this.oauthService.customQueryParams = {
      'kc_idp_hint': identificationProvider
    };

    await this.oauthService.loadDiscoveryDocumentAndLogin();
  }

  public setUser(user: User | null): void {
    this.user.next(user);
  }

  public getUser(): Observable<User | null> {
    if (!this.isLoggedIn()) {
      this.setUser(null);
    }
    return this.user.asObservable();
  }
  
  public isAdmin() {
    return this.user.getValue()?.formsAdmin || false;
  }

  public isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public get isLoggedInAsync(): Observable<boolean> {
    return this.isAuthenticated.asObservable();
  }

  public async logOut(customMessage?: string): Promise<void> {
    const message = customMessage || this.translateService.instant(
      'components.user-logout.confirmation'
    );
    const confirmed = await this.dialogService.confirm({
      message: message,
      width: '400px',
    });
    if (confirmed) {
      this.oauthService.redirectUri = window.location.href;
      this.oauthService.logOut();
      this.setUser(null);
    }
  }

  public autofill(control: AbstractControl | null | undefined, value: any, lock = true) {
    if(value && control) {
      control.setValue(value);
      control.autofilled = true;
      if (lock) {
        control.readonly = true;
      }
    }
  }

}